import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';

const COLORS = {
  purple: '#6C63FF', purpleLight: '#F5F3FF', purpleBorder: '#DDD6FE',
  text1: '#1E293B', text2: '#64748B', text3: '#94A3B8',
  bg: '#F8FAFC', white: '#FFFFFF', border: '#E2E8F0',
  green: '#10B981', red: '#EF4444',
};

const MONTHS = ['Yanvar','Fevral','Mart','Aprel','May','İyun','İyul','Avqust','Sentyabr','Oktyabr','Noyabr','Dekabr'];

export default function CorporateReportPage() {
  const { business } = useAuth();
  const { activeLocation } = useLocation();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year,  setYear]  = useState(new Date().getFullYear());
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    const locId = activeLocation?.id || business?.id;
    if (!locId) return;
    setLoading(true);
    const from = `${year}-${String(month).padStart(2,'0')}-01`;
    const to   = month === 12
      ? `${year + 1}-01-01`
      : `${year}-${String(month + 1).padStart(2,'0')}-01`;

    const { data: txns } = await supabase
      .from('corporate_meal_transactions')
      .select('*, corporate_partners:corporate_partner_id(company_name)')
      .eq('business_location_id', locId)
      .gte('created_at', from)
      .lt('created_at', to)
      .order('created_at', { ascending: false });

    if (txns?.length) {
      const userIds = [...new Set(txns.map(t => t.user_id).filter(Boolean))];
      const { data: users } = await supabase
        .from('public_user_profiles')
        .select('id, full_name')
        .in('id', userIds);
      const userMap = {};
      (users || []).forEach(u => { userMap[u.id] = u; });
      setTransactions(txns.map(t => ({ ...t, users: userMap[t.user_id] || null })));
    } else {
      setTransactions([]);
    }
    setLoading(false);
  }, [month, year, activeLocation?.id, business?.id]);

  useEffect(() => { load(); }, [load]);

  const totalAmount = transactions.reduce((s, t) => s + (t.amount || 0), 0);

  // Şirkətlər üzrə qruplaşdır
  const byPartner = transactions.reduce((acc, t) => {
    const name = t.corporate_partners?.company_name || 'Naməlum';
    if (!acc[name]) acc[name] = { count: 0, total: 0 };
    acc[name].count++;
    acc[name].total += t.amount || 0;
    return acc;
  }, {});

  const exportCSV = () => {
    const rows = [
      ['Tarix', 'Ad', 'Email', 'Şirkət', 'Məbləğ (₼)', 'Əvvəlki Balans', 'Sonrakı Balans'],
      ...transactions.map(t => [
        new Date(t.created_at).toLocaleDateString('az-AZ'),
        t.users?.full_name  || '-',
        t.users?.email      || '-',
        t.corporate_partners?.company_name || '-',
        t.amount?.toFixed(2),
        t.balance_before?.toFixed(2),
        t.balance_after?.toFixed(2),
      ]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `korporativ-${year}-${month}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: 28, background: COLORS.bg, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: COLORS.text1, margin: 0 }}>🏢 Korporativ Hesabat</h1>
          <p style={{ fontSize: 13, color: COLORS.text2, marginTop: 4 }}>Yemək büdcəsi əməliyyatları</p>
        </div>
        <button onClick={exportCSV} disabled={!transactions.length} style={{
          padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer',
          background: transactions.length ? COLORS.purple : COLORS.border,
          color: transactions.length ? '#fff' : COLORS.text3,
          fontWeight: 700, fontSize: 13,
        }}>⬇ CSV Export</button>
      </div>

      {/* Ay / İl seçici */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <select value={month} onChange={e => setMonth(Number(e.target.value))}
          style={{ padding: '9px 14px', borderRadius: 9, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: 'none', background: '#fff' }}>
          {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
        </select>
        <select value={year} onChange={e => setYear(Number(e.target.value))}
          style={{ padding: '9px 14px', borderRadius: 9, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: 'none', background: '#fff' }}>
          {[2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {msg && <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 10, fontWeight: 600, fontSize: 14,
        background: msg.startsWith('✅') ? '#F0FDF4' : '#FEF2F2',
        color: msg.startsWith('✅') ? COLORS.green : COLORS.red }}>{msg}</div>}

      {/* Xülasə kartları */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Ümumi Dövriyyə', value: `${totalAmount.toFixed(2)} ₼`, icon: '💳', color: COLORS.purple },
          { label: 'Əməliyyat sayı', value: transactions.length, icon: '🔢', color: COLORS.text1 },
          { label: 'Şirkət sayı', value: Object.keys(byPartner).length, icon: '🏢', color: COLORS.green },
        ].map((s, i) => (
          <div key={i} style={{ background: COLORS.white, borderRadius: 12, padding: '18px 20px', border: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: 22 }}>{s.icon}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: s.color, marginTop: 6 }}>{s.value}</div>
            <div style={{ fontSize: 13, color: COLORS.text2, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Şirkət üzrə xülasə */}
      {Object.keys(byPartner).length > 0 && (
        <div style={{ background: COLORS.white, borderRadius: 14, border: `1px solid ${COLORS.border}`, padding: 20, marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: COLORS.text1, margin: '0 0 14px' }}>Şirkət üzrə xülasə</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Object.entries(byPartner).map(([name, d]) => (
              <div key={name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: COLORS.purpleLight, borderRadius: 10, padding: '10px 16px',
                border: `1px solid ${COLORS.purpleBorder}` }}>
                <div>
                  <span style={{ fontWeight: 700, color: COLORS.text1, fontSize: 14 }}>{name}</span>
                  <span style={{ fontSize: 12, color: COLORS.text2, marginLeft: 8 }}>{d.count} əməliyyat</span>
                </div>
                <span style={{ fontWeight: 800, color: COLORS.purple, fontSize: 16 }}>{d.total.toFixed(2)} ₼</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ətraflı cədvəl */}
      <div style={{ background: COLORS.white, borderRadius: 14, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: COLORS.text1, margin: 0 }}>Yemək Kartı Əməliyyatları</h3>
          <span style={{ fontSize: 13, color: COLORS.text2 }}>{MONTHS[month-1]} {year}</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F8FAFC' }}>
              {['Tarix', 'Ad', 'Şirkət', 'Məbləğ'].map(h => (
                <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700,
                  color: COLORS.text2, textTransform: 'uppercase', letterSpacing: '0.5px',
                  borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} style={{ padding: 40, textAlign: 'center', color: COLORS.text3 }}>Yüklənir...</td></tr>
            ) : transactions.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: 40, textAlign: 'center', color: COLORS.text3 }}>
                {MONTHS[month-1]} {year} üçün əməliyyat yoxdur
              </td></tr>
            ) : transactions.map(t => (
              <tr key={t.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: '12px 16px', fontSize: 13, color: COLORS.text2 }}>
                  {new Date(t.created_at).toLocaleDateString('az-AZ')}
                  <div style={{ fontSize: 11, color: COLORS.text3 }}>
                    {new Date(t.created_at).toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: COLORS.text1 }}>{t.users?.full_name || '-'}</div>
                  <div style={{ fontSize: 11, color: COLORS.text3 }}>{t.users?.email || ''}</div>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ background: COLORS.purpleLight, color: COLORS.purple,
                    fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 6 }}>
                    {t.corporate_partners?.company_name || '-'}
                  </span>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ fontWeight: 800, color: COLORS.red, fontSize: 15 }}>−{t.amount?.toFixed(2)} ₼</span>
                </td>
              </tr>
            ))}
          </tbody>
          {transactions.length > 0 && (
            <tfoot>
              <tr style={{ background: COLORS.purpleLight }}>
                <td colSpan={3} style={{ padding: '12px 16px', fontWeight: 700, color: COLORS.text1 }}>CƏMİ</td>
                <td style={{ padding: '12px 16px', fontWeight: 800, color: COLORS.red, fontSize: 16 }}>
                  −{totalAmount.toFixed(2)} ₼
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
